import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * The models that stand on the map: a pine for most woodland, a broadleaf tree
 * for the warm biomes, a rock for stone.
 *
 * Loaded once and then instanced — a wood is thousands of copies of one tree,
 * and the only version of that which draws in one call per material is an
 * `InstancedMesh`. That is also why each model is flattened here into a list of
 * **parts**: an instanced mesh takes exactly one material, and a model is free
 * to arrive with several — the pines that used to stand here had bark and
 * needles as two. Today's is a single mesh with its colour, normal and
 * roughness in three textures on one material, so it draws as one call; the
 * parts of a model share their transforms either way.
 *
 * `pines` stays a **list** with one entry in it. Which model a cell gets is
 * already decided by its coordinates, so a second pine is a file and a line
 * here rather than a change to how anything is placed — and the variety that
 * one model has to carry alone in the meantime comes from the size and the
 * rotation, which are per prop anyway.
 *
 * # Why the scale is measured rather than written down
 *
 * The files arrive at whatever size they were exported at — the pine has been
 * swapped once already and came back a different height, and the rock comes
 * through a node scaled by a hundred. Hard-coding a factor per file would mean
 * re-deriving it every time a model is swapped. Instead each model is measured
 * on load, its base is put on the ground and its height normalised to one, so
 * the world says how tall a tree is in world units and the file has no say
 * in it.
 */

export interface PropPart {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
}

export interface PropModel {
  name: string;
  parts: PropPart[];
  /**
   * How wide the model is when it is one unit tall.
   *
   * Normalising height alone is not enough. Measured on the two pines that used
   * to stand here — the same tree drawn at two proportions — the squat one came
   * out a third wider than a hex once both were made the same height. The
   * placement uses this to cap the width, so a model swapped for a squatter one
   * gets shorter rather than spilling across its neighbours; that is exactly
   * what a swapped file is free to do, and it has happened.
   */
  footprint: number;
}

export interface Props {
  /** Conifers: the default tree, and what a wood is made of nearly everywhere. */
  pines: PropModel[];
  /**
   * Broadleaf trees, for the biomes a pine has no business standing in.
   *
   * The rainforest and the savanna are the two the world already names, and a
   * pine in either of them reads as a mistake before anything else about the
   * scene registers. Which biomes these are is decided by the caller — a list
   * of ids here would go stale the day one is added.
   */
  broadleaf: PropModel[];
  rocks: PropModel[];
  /**
   * Przegroda na nieprzekraczalnej krawędzi — **jeden model na całą krawędź**.
   *
   * Nie w `rocks`, bo to nie jest głaz do rozsypania: ma swoją długość i swój
   * kierunek, a stawia się go jeden, wzdłuż granicy. Trzymanie go w tamtej
   * liście znaczyłoby, że może wypaść na polu jako zwykły kamień.
   */
  border: PropModel;
}

/**
 * Props are solid, whatever the file says.
 *
 * A pine exported from Blender arrives with `alphaMode: "BLEND"` on its
 * material even though its texture has not one pixel of transparency in it —
 * alpha is 255 everywhere. GLTFLoader does the correct thing with that and
 * turns **off the depth write**, because a blended surface that writes depth
 * hides what is behind it. The result on screen is that trees stop occluding
 * one another and every trunk shows through the canopy in front of it.
 *
 * So the flag is overruled here rather than in the file: a tree, a rock and
 * anything else that stands on the ground is opaque, and an instanced mesh
 * could not blend correctly anyway — blending needs the copies sorted back to
 * front and an `InstancedMesh` draws them in buffer order.
 *
 * `alphaTest` rather than nothing, in case a future model really does carry
 * cut-out foliage: a leaf card then gets a hard edge and correct depth, which
 * is what such a model wants in the first place. On a texture with no alpha it
 * changes nothing.
 */
function solid(material: THREE.Material): THREE.Material {
  material.transparent = false;
  material.depthWrite = true;
  material.alphaTest = 0.5;
  return material;
}

/**
 * One model, flattened: node transforms baked into the geometry, base at the
 * origin, one unit tall.
 *
 * Exported for the measurements, which load a file and check what came out of
 * it rather than trusting that it was what the exporter meant.
 */
export function flatten(name: string, scene: THREE.Object3D): PropModel {
  const parts: PropPart[] = [];

  scene.updateWorldMatrix(true, true);
  scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;

    // The node scale is baked in rather than kept: an instanced mesh carries
    // one matrix per copy, and there is nowhere in it for a parent's transform.
    const geometry = object.geometry.clone();
    geometry.applyMatrix4(object.matrixWorld);

    const materials = Array.isArray(object.material) ? object.material : [object.material];
    parts.push({ geometry, material: solid(materials[0]) });
  });

  const bounds = new THREE.Box3();
  for (const part of parts) {
    part.geometry.computeBoundingBox();
    bounds.union(part.geometry.boundingBox!);
  }

  const height = Math.max(bounds.max.y - bounds.min.y, 1e-6);
  const middle = new THREE.Vector3((bounds.min.x + bounds.max.x) / 2, 0, (bounds.min.z + bounds.max.z) / 2);

  for (const part of parts) {
    // Centred on its own footprint and standing on zero, so a placement is a
    // position on the ground rather than a position plus an offset nobody can
    // see in the file.
    part.geometry.translate(-middle.x, -bounds.min.y, -middle.z);
    part.geometry.scale(1 / height, 1 / height, 1 / height);
    part.geometry.computeBoundingSphere();
  }

  const footprint = Math.max(bounds.max.x - bounds.min.x, bounds.max.z - bounds.min.z) / height;

  return { name, parts, footprint };
}

export async function loadProps(): Promise<Props> {
  const loader = new GLTFLoader();

  const load = async (file: string): Promise<PropModel> => {
    const gltf = await loader.loadAsync(`/models/${file}.glb`);
    return flatten(file, gltf.scene);
  };

  // **Kamienie są trzy i to jest cała zmiana, jakiej wymagało ich dołożenie.**
  // Który stanie na polu, rozstrzyga hash położenia — ta sama funkcja, która
  // wybiera obrót i wielkość — więc czwarty plik to czwarty wiersz tej listy
  // i nic więcej. Lista istniała od początku właśnie po to.
  const rockFiles = ['rock-1', 'rock-2', 'rock-3'];

  const [pine, tree, border, ...rocks] = await Promise.all([
    load('pine-1'),
    load('tree-1'),
    load('stone-border-1'),
    ...rockFiles.map(load),
  ]);

  return { pines: [pine], broadleaf: [tree], rocks, border };
}
